import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Input, Button, MessagePlugin } from 'tdesign-react';
import styled from 'styled-components';
import { MinusCircleIcon, PlusIcon } from 'tdesign-icons-react';
import { isEmpty, isEqual } from 'lodash';
import { useMemoizedFn } from 'ahooks';
import CodeMirrorLight from '@/components/CodeMirrorLight';
import Form from '@/components/Form';
import { isVoid, sleep } from '@/utils/common';
import Text from '@/components/Text';

// 如果存在重复的值，返回true
function hasDuplicates(array) {
  return new Set(array).size !== array.length;
}

/**
 * @typedef {import("./type").Props} Props
 */

/**
 * JSON-FORM 可切换输入框
 * @param {Props} props
 * @returns
 */
export default function JSONConverterInput({
  value,
  onChange,
  jsonOnly,
  helper,
  immutable,
  ...others
}) {
  const [form] = Form.useForm();
  const [currentTab, setCurrentTab] = useState(jsonOnly ? 'json' : 'form');

  const jsonArray = Form.useWatch('json', form);

  const json = useMemo(() => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }, [value]);

  // 重置表单到最新值
  const resetFormFields = useMemoizedFn(() => {
    form.setFieldValue('json', json ? Object.entries(json) : [['', '']]);
  });

  // 等待 30ms 后比对json，判断是否有变化（如有onChange等触发）
  const waithAndCompare = useMemoizedFn(async () => {
    await sleep(30);
    // 剔除空项
    const noEmptyJSONArray = jsonArray?.filter((item) => !isVoid(item[0])) || [];
    const hasChanged = !isEqual(json || {}, Object.fromEntries(noEmptyJSONArray));
    if (hasChanged) {
      resetFormFields();
    }
  });

  useEffect(() => {
    waithAndCompare();
  }, [waithAndCompare, json]);

  useEffect(() => {
    if (currentTab === 'form') {
      resetFormFields();
    }
  }, [resetFormFields, currentTab]);

  const onValuesChange = (_, values) => {
    const json = values.json?.filter((item) => !isVoid(item[0])); // 忽略空key情况

    const keys = [];
    for (const [key] of json) {
      keys.push(key);
    }
    if (hasDuplicates(keys)) {
      MessagePlugin.warning('存在相同key，解析失败');
      return;
    }

    onChange(isEmpty(json) ? '' : JSON.stringify(Object.fromEntries(json), null, 2));
  };

  return (
    <Root>
      <Tabs value={currentTab} theme='card' onChange={setCurrentTab}>
        {!jsonOnly && (
          <Tabs.TabPanel value={'form'} label='表单填写'>
            <Form
              component='div'
              form={form}
              style={{ padding: '20px 24px 12px' }}
              onValuesChange={onValuesChange}
            >
              <Form.List name='json'>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => (
                      <ListContainer key={index}>
                        <Form.FormItem
                          className={immutable && 'immutable'}
                          {...restField}
                          name={[name, 0]}
                        >
                          {immutable ? <Text /> : <Input placeholder='key' />}
                        </Form.FormItem>
                        <Form.FormItem {...restField} name={[name, 1]}>
                          <Input placeholder='value' />
                        </Form.FormItem>
                        {fields.length > 1 && !immutable && (
                          <MinusCircleIcon
                            size='20px'
                            style={{ cursor: 'pointer', color: 'var(--td-text-color-placeholder)' }}
                            onClick={() => remove(name)}
                          />
                        )}
                      </ListContainer>
                    ))}
                    {!immutable && (
                      <Button
                        theme='primary'
                        variant='text'
                        icon={<PlusIcon />}
                        onClick={() => add(['', ''])}
                      >
                        添加
                      </Button>
                    )}
                  </>
                )}
              </Form.List>
            </Form>
          </Tabs.TabPanel>
        )}
        {!immutable && (
          <Tabs.TabPanel value={'json'} label='JSON填写'>
            <CodeMirrorLight value={value || ''} onChange={onChange} {...others} type='json' />
          </Tabs.TabPanel>
        )}
      </Tabs>
    </Root>
  );
}

const Root = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 139px;
  .t-tabs__nav {
    min-height: 0 !important;
    .t-tabs__nav-container {
      background-color: #fff;
      .t-tabs__nav--card.t-tabs__nav-item {
        background-color: #fff;
        border-left: 1px solid rgb(237, 237, 237);
        border-right: 1px solid rgb(237, 237, 237);
        border-top: 1px solid rgb(237, 237, 237);
        border-radius: 6px 6px 0px 0px;
        margin-right: 10px;
        &.t-is-active {
          background-color: #fafafa;
        }
      }
      .t-tabs__nav-item.t-size-m {
        height: var(--td-comp-size-l);
        line-height: var(--td-comp-size-l);
      }
    }
  }

  .t-tabs__content {
    position: relative;
    border: 1px solid rgba(0, 0, 0, 0.05);
    margin-top: -1px;
    z-index: 1;
    border-radius: 0px 0 6px 6px;
    background-color: #fafafa;
  }

  .cm-editor {
    min-height: 100px;
    background-color: #fafafa;
  }
`;

const ListContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  .t-form__item {
    flex: 1;
    min-width: 0;
    margin-right: 0;
  }
`;
